/**
 * 会员中心。
 *
 * ⚠️ 后端 `membership.update` 直接采信客户端传来的 `vip` 布尔值
 * （`const vip = Boolean(payload.vip)` 后即写库，无支付校验），
 * 任何已登录用户调用一次就能白拿会员。
 * 因此本页**不提供**任何直接调用它的入口 —— 开通只走下单，
 * 会员状态应由支付回调置位。修复前请勿在前端接这个 action。
 *
 * 支付通道尚未接入（payments.callback 有实现，但没有任何支付服务商对接），
 * 所以下单只生成 pending 订单，页面如实告知用户需人工确认。
 */
import { useEffect, useState } from "react";
import { callApi, getSession } from "../api";
import { useMembership } from "../hooks/useMembership";
import { site } from "../data/site";

type Order = {
  id?: string;
  productName?: string;
  amount?: number;
  currency?: string;
  status?: string;
  createdAt?: string;
};

const PLANS = [
  { product: "vip-month", productName: "月度会员", amount: 39 },
  { product: "vip-year", productName: "年度会员", amount: 299 },
];

const STATUS_TEXT: Record<string, string> = {
  pending: "待支付",
  paid: "已支付",
  refunded: "已退款",
  cancelled: "已取消",
};

export function VipPage({ onNavigate }: { onNavigate: (route: string) => void }) {
  const { membership, isVip, signedIn, reload } = useMembership();
  const [orders, setOrders] = useState<Order[]>([]);
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState("");

  useEffect(() => {
    if (!getSession()) return;
    callApi<{ orders?: Order[] }>("orders.list").then((result) => {
      if (result.ok) setOrders(result.orders ?? []);
    });
  }, []);

  async function order(plan: (typeof PLANS)[number]) {
    if (!getSession()) {
      onNavigate("auth");
      return;
    }
    setBusy(plan.product);
    setMessage("");
    const result = await callApi<{ order?: Order }>("orders.create", {
      order: { ...plan, currency: "CNY", status: "pending", channel: "manual" },
    });
    setBusy("");
    if (!result.ok) {
      setMessage(`下单失败：${result.error}`);
      return;
    }
    if (result.order) setOrders((prev) => [result.order as Order, ...prev]);
    setMessage(`已生成待支付订单，请通过 ${site.email} 联系客服完成开通。`);
    void reload();
  }

  return (
    <main className="page vip-page">
      <h1>会员中心</h1>
      <p className="page-summary">会员可查看全部名人案例与进阶学习资料。</p>

      {!signedIn ? (
        <p className="empty-state">
          请先
          <button type="button" onClick={() => onNavigate("auth")}>
            登录
          </button>
          查看会员状态。
        </p>
      ) : (
        <section className="vip-status">
          <h2>当前状态</h2>
          <p>
            {isVip ? "会员生效中" : "普通用户"}
            {membership?.plan && <span className="vip-plan">套餐：{membership.plan}</span>}
          </p>
        </section>
      )}

      <section className="vip-plans">
        <h2>可选套餐</h2>
        <div className="plan-grid">
          {PLANS.map((plan) => (
            <div key={plan.product} className="plan-card">
              <strong>{plan.productName}</strong>
              <span className="plan-price">¥{plan.amount}</span>
              <button type="button" onClick={() => order(plan)} disabled={busy === plan.product}>
                {busy === plan.product ? "提交中…" : "下单"}
              </button>
            </div>
          ))}
        </div>
        <p className="vip-hint">
          在线支付通道尚未开通，下单后订单为「待支付」状态，需联系客服人工确认后开通。
        </p>
        {message && <p className="vip-message">{message}</p>}
      </section>

      {orders.length > 0 && (
        <section className="vip-orders">
          <h2>我的订单</h2>
          <ul className="order-list">
            {orders.map((item, index) => (
              <li key={item.id ?? index}>
                <strong>{item.productName ?? "会员套餐"}</strong>
                <span>
                  {item.currency === "CNY" ? "¥" : ""}
                  {item.amount ?? 0}
                </span>
                <span className="order-status">{STATUS_TEXT[item.status ?? ""] ?? item.status}</span>
                {item.createdAt && <time>{item.createdAt.slice(0, 10)}</time>}
              </li>
            ))}
          </ul>
        </section>
      )}
    </main>
  );
}
